"use client";

import { Box, Spinner, useToast, Text, UnorderedList, ListItem, Alert, AlertIcon, useDisclosure } from "@chakra-ui/react";

import { useFormik } from "formik";
import { useState } from "react";
import { API_BASE_URL } from "../components/APIs";
import axios from "axios";
import Cookies from "js-cookie";
import { CustomButton } from "../components/Buttons";

// Confirmation Dialog Component
const ConfirmationDialog = ({ isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      width="100%"
      height="100%"
      backgroundColor="rgba(0, 0, 0, 0.5)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex="1000"
    >
      <Box
        backgroundColor="white"
        padding="6"
        borderRadius="md"
        boxShadow="lg"
        maxWidth="500px"
        width="90%"
        mx="auto"
      >
        <Text fontSize="xl" fontWeight="bold" mb="3">
          Confirm Submission
        </Text>
        
        <Alert status="warning" mb="4" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Important Notice</Text>
            <Text fontSize="sm">
              Once you submit your application, you cannot edit or make any changes to your data.
              Please ensure all information is correct before proceeding.
            </Text>
          </Box>
        </Alert>

        <Text mb="4">
          Are you sure you want to submit your application? This action cannot be undone.
        </Text>

        <Box display="flex" gap="3" justifyContent="flex-end">
          <CustomButton
            onClick={onClose}
            bg="gray.400"
            colorScheme="gray"
            label="Cancel"
            isDisabled={isLoading}
          />
          <CustomButton
            onClick={onConfirm}
            bg="blue.400"
            colorScheme="blue"
            label={isLoading ? "Submitting..." : "Yes, Submit Application"}
            isLoading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default function SubmitApplication() {
  const [isLoading, setLoading] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [isSubmitting, setSubmitting] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const uid = sessionStorage.getItem("uid");
  const toast = useToast();
 
  const fieldLabels = {
    nok: "Next of Kin",
    mobility_status: "Mobility Status",
    personal_info: "Personal Information",
    poi: "Proof of Identity",
    poa: "Proof of Address",
    rtw: "Right to Work",
    work_history: "Work History",
    resume: "Resume",
    certificates: "Certificates",
    passport: "Passport Photo"
  };

  const handleSubmitApplication = (data) => {
    setSubmitting(true);
    const url = API_BASE_URL + "/application/submitApplication";
    axios
      .post(url, data)
      .then((response) => {
        setSubmitting(false);
        setLoading(false);
        onClose();
        
        if (response.data.status === 200) {
          toast({
            title: "Application Submitted",
            description: response.data.message,
            position: "top",
            status: "success",
            isClosable: true,
          });
          setMissingFields([]);
          window.location.href = "/application-submitted";
        } else {
          toast({
            title: "Application Incomplete",
            description: response.data.message,
            position: "top",
            status: "error",
            isClosable: true,
          });
          if (response.data.missing_fields) {
            const missing = Object.entries(response.data.missing_fields)
              .filter(([_, value]) => value === 0)
              .map(([key]) => fieldLabels[key] || key.replace("_", " ").toUpperCase());
            setMissingFields(missing);
          }
        }
      })
      .catch(() => {
        setSubmitting(false);
        setLoading(false);
        onClose();
        toast({
          title: "Error",
          description: "Something went wrong, please try again.",
          position: "top",
          status: "error",
          isClosable: true,
        });
      });
  };

  const handleSubmitClick = () => {
    onOpen();
  };

  const handleConfirmSubmit = () => {
    handleSubmitApplication(formik.values);
  };

  const formik = useFormik({
    initialValues: {
      user_id: uid,
    },
    onSubmit: handleSubmitApplication,
  });

  return (
    <>
      <Box my={3}>
        {/* Warning Note */}
        <Alert status="info" mb="4" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <strong>Note:</strong> After submission, you cannot edit or make any changes to your application data. 
            Please review all information carefully before submitting.
          </Text>
        </Alert>

        {missingFields.length > 0 && (
          <Box mb={3} p={3} borderWidth={1} borderColor="red.400" borderRadius="md" backgroundColor="red.50">
            <Text fontWeight="bold" color="red.600">Incomplete Application Fields:</Text>
            <UnorderedList>
              {missingFields.map((field, index) => (
                <ListItem key={index} color="red.500">{field}</ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}
        
        {isLoading ? (
          <Spinner
            thickness="3px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="md"
          />
        ) : (
          <CustomButton
            type="button"
            onClick={handleSubmitClick}
            bg={"blue.400"}
            colorScheme="blue"
            label="Submit Application"
          />
        )}
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmSubmit}
        isLoading={isSubmitting}
      />
    </>
  );
}